import { forwardRef, Fragment, useMemo, useRef, useState } from "react";
import { ChevronDown, Plus, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { Segmented } from "./primitives";
import ValueRow from "./ValueRow";
import useRowSweep from "./useRowSweep";
import { groupProperties, isFilled, VALUES_PREVIEW_ROWS, VALUES_PREVIEW_THRESHOLD } from "./values";

/**
 * The value list's view state: the property filter, the Missing toggle, the
 * collapsed category groups, and whether a long list is open past its
 * preview.
 *
 * ValuesPanel keeps its own when it is not handed one. The editor holds it
 * itself (and hands it down) so it outlives the Overview tab's content, which
 * unmounts while another tab is open, and so the summary rail's "show"
 * (`showMissing`) and the page's reset of `open` per passport can reach it.
 */
export function useValuesPanelState(initialOpen = true) {
    const [query, setQuery] = useState('');
    const [missingOnly, setMissingOnly] = useState(false);
    const [collapsedGroups, setCollapsedGroups] = useState(() => new Set());
    const [open, setOpen] = useState(initialOpen);

    const toggleGroup = (key) => setCollapsedGroups((current) => {
        const next = new Set(current);
        next.has(key) ? next.delete(key) : next.add(key);
        return next;
    });

    const showMissing = () => {
        setQuery('');
        setMissingOnly(true);
    };

    return { query, setQuery, missingOnly, setMissingOnly, collapsedGroups, toggleGroup, open, setOpen, showMissing };
}

/**
 * A passport's values (the editor's C4 section): the company assigned ID row,
 * then every property grouped under its category, each group with a set/total
 * meter and a collapse toggle; a property filter and an All/Missing switch;
 * and, for a long list, a three-row preview behind "Show all N properties".
 *
 * Props, data in and markup out:
 * - `properties`: the level's ontology properties in order, category rows
 *   included (`ontology.data[depth].properties`).
 * - `values`: the passport's stored values, `{ [propertyId]: { value } }`.
 * - `friendlyId`, `readOnly`: the company assigned ID row, and the lock.
 * - `renderValue(property, index)`: one row. The editor passes ValueField
 *   (row plus editing drawer); the default is a bare ValueRow built from
 *   `values`, `readOnly`, `reportedTier`, `language` and `defaultLanguage`,
 *   which is what a marketing vignette wants (no drawer, no file browser).
 * - `companyIdControl(trigger)`: wraps the row's "Assign" button in the
 *   editor's company ID popover; without it the button renders bare.
 * - `state`: a useValuesPanelState() to render from; without it the panel
 *   keeps its own, opening a list of more than five properties as a preview
 *   (`defaultOpen` overrides that).
 * - `filterRef`: the filter input, for the page's "/" shortcut.
 * - `idPrefix`: the heading and list ids (`values-heading`, `values-list`);
 *   change it when two panels share a page.
 * - `dayFirst`: date values day first (ValueRow's), for the marketing
 *   site's vignettes; off by default.
 * - `sweepKey`: sweeps the rows in as they load (useRowSweep), again
 *   whenever it changes; the editor passes the passport's snowflake. Left
 *   out (the vignettes, which move on their own script), rows just appear.
 * - `editing`: the id of the property whose editor is open. The Missing
 *   filter keeps its row (and so its group) while it is: a value filled in
 *   meanwhile, by someone else, would otherwise unmount the editor and what
 *   was typed in it. Null by default.
 *
 * The forwarded ref lands on the `<section>` (the rail scrolls it into view).
 */
const ValuesPanel = forwardRef(function ValuesPanel({
    properties,
    values,
    friendlyId = null,
    readOnly = false,
    renderValue,
    companyIdControl,
    state,
    defaultOpen,
    filterRef,
    reportedTier = null,
    language = null,
    defaultLanguage,
    idPrefix = 'values',
    dayFirst = false,
    sweepKey,
    editing = null,
}, ref) {
    const passportProperties = properties ?? [];
    const groups = useMemo(() => groupProperties(passportProperties), [passportProperties]);
    const fields = groups.flatMap((group) => group.items);

    const ownState = useValuesPanelState(defaultOpen ?? fields.length <= VALUES_PREVIEW_THRESHOLD);
    const { query: propertySearch, setQuery: setPropertySearch, missingOnly, setMissingOnly, collapsedGroups, toggleGroup, open: valuesOpen, setOpen: setValuesOpen } = state ?? ownState;

    const isSet = (property) => isFilled(values?.[property.id]?.value);
    const setCount = fields.filter(({ property }) => isSet(property)).length;
    const missingCount = fields.length - setCount;

    const propertyQuery = propertySearch.trim().toLowerCase();
    const filtering = propertyQuery !== '' || missingOnly;
    const visibleGroups = groups.map((group) => ({
        ...group,
        visible: group.items.filter(({ property }) => (
            (!propertyQuery || String(property.name ?? '').toLowerCase().includes(propertyQuery))
            && (!missingOnly || !isSet(property) || (editing !== null && property.id === editing))
        )),
    })).filter((group) => !filtering || group.visible.length > 0);

    // Looking for something always shows everything it could be.
    const valuesExpanded = valuesOpen || filtering;

    const listRef = useRef(null);
    useRowSweep(listRef, { key: sweepKey, enabled: sweepKey !== undefined, paused: filtering });

    const renderRow = renderValue ?? ((property) => (
        <ValueRow
            property={property}
            value={values?.[property.id]?.value}
            reportedTier={reportedTier}
            disabled={readOnly}
            language={language}
            defaultLanguage={defaultLanguage}
            dayFirst={dayFirst}
        />
    ));
    const row = ({ property, index }) => <Fragment key={property.id ?? index}>{renderRow(property, index)}</Fragment>;

    const assignButton = (
        <button type="button" className="inline-flex items-center gap-1 rounded-sm text-zinc-400 transition-colors hover:text-zinc-900">
            <Plus className="size-3.5" aria-hidden="true" />Assign
        </button>
    );

    const headingId = `${idPrefix}-heading`;
    const listId = `${idPrefix}-list`;

    return (
        <section ref={ref} aria-labelledby={headingId} className="scroll-mt-6">
            <div className="mb-2 flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                    <h2 id={headingId} className="text-[14px] font-semibold text-zinc-950">Values</h2>
                    {fields.length > VALUES_PREVIEW_ROWS && !filtering && (
                        <button
                            type="button"
                            onClick={() => setValuesOpen((open) => !open)}
                            aria-expanded={valuesExpanded}
                            aria-controls={listId}
                            aria-label={valuesExpanded ? 'Collapse values' : 'Expand values'}
                            className="inline-flex size-6 items-center justify-center rounded-md text-zinc-400 transition-colors hover:bg-zinc-200/60 hover:text-zinc-900"
                        >
                            <ChevronDown className={cn('size-3.5 transition-transform', !valuesExpanded && '-rotate-90')} aria-hidden="true" />
                        </button>
                    )}
                </div>
                {fields.length > 0 && <span className="text-[12px] tabular-nums text-zinc-500">{setCount} of {fields.length} set</span>}
            </div>
            {passportProperties.length > 0 && (
                <div className="mb-3 flex flex-wrap items-center gap-2">
                    <div className="relative w-full max-w-xs">
                        <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-zinc-400" aria-hidden="true" />
                        <input
                            ref={filterRef}
                            type="search"
                            aria-label="Filter properties"
                            placeholder="Filter properties"
                            value={propertySearch}
                            onChange={(event) => setPropertySearch(event.target.value)}
                            className="h-8 w-full rounded-md border-0 bg-white pl-8 pr-8 text-[13px] ring-1 ring-inset ring-zinc-200 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900/15"
                        />
                        <kbd className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 rounded border border-zinc-200 bg-white px-1 font-mono text-[10.5px] text-zinc-500">/</kbd>
                    </div>
                    <Segmented
                        label="Show"
                        value={missingOnly ? 'missing' : 'all'}
                        onChange={(value) => setMissingOnly(value === 'missing')}
                        options={[
                            { value: 'all', label: 'All', count: fields.length },
                            { value: 'missing', label: 'Missing', count: missingCount },
                        ]}
                    />
                </div>
            )}
            <div id={listId} ref={listRef} className="overflow-hidden rounded-lg bg-white ring-1 ring-zinc-950/[.07]">
                <dl className="divide-y divide-zinc-100">
                    {(!filtering || 'company assigned id'.includes(propertyQuery)) && !missingOnly && (
                        <div className="grid min-h-10 grid-cols-[minmax(0,1fr)_28px] items-center gap-x-4 gap-y-1 px-3 py-1.5 sm:grid-cols-[minmax(140px,240px)_minmax(0,1fr)_28px]">
                            <dt className="col-start-1 row-start-1 text-[13px] text-zinc-500">Company assigned ID</dt>
                            <dd className="col-start-1 row-start-2 min-w-0 text-[13px] sm:col-start-2 sm:row-start-1">
                                {friendlyId ? (
                                    <span className="font-mono text-[12.5px] text-zinc-950">{friendlyId}</span>
                                ) : readOnly ? (
                                    <span className="text-zinc-400">Not assigned</span>
                                ) : companyIdControl ? companyIdControl(assignButton) : assignButton}
                            </dd>
                        </div>
                    )}
                </dl>
                {!valuesExpanded && (
                    <>
                        <dl className="divide-y divide-zinc-100 border-t border-zinc-100">
                            {fields.slice(0, VALUES_PREVIEW_ROWS).map(row)}
                        </dl>
                        <button
                            type="button"
                            onClick={() => setValuesOpen(true)}
                            className="flex h-9 w-full items-center justify-center gap-1.5 border-t border-zinc-100 bg-zinc-50/60 text-[12.5px] font-medium text-zinc-600 transition-colors hover:bg-zinc-100/70 hover:text-zinc-950"
                        >
                            Show all {fields.length} properties
                            <ChevronDown className="size-3.5" aria-hidden="true" />
                        </button>
                    </>
                )}
                {valuesExpanded && visibleGroups.map((group) => {
                    const collapsed = !filtering && collapsedGroups.has(group.key);
                    const groupSet = group.items.filter(({ property }) => isSet(property)).length;
                    return (
                        <div key={group.key}>
                            {group.title && (
                                <button
                                    type="button"
                                    onClick={() => toggleGroup(group.key)}
                                    aria-expanded={!collapsed}
                                    className="flex h-9 w-full items-center gap-2 border-y border-zinc-200/70 bg-zinc-50/80 px-3 text-left text-[12px] font-semibold text-zinc-700 transition-colors hover:bg-zinc-100/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-zinc-900/15"
                                >
                                    <ChevronDown className={cn('size-3.5 text-zinc-400 transition-transform', collapsed && '-rotate-90')} aria-hidden="true" />
                                    {group.title}
                                    {group.items.length > 0 && (
                                        <span className="ml-auto inline-flex items-center gap-2 font-normal tabular-nums text-zinc-500">
                                            <span className="h-1 w-16 overflow-hidden rounded-full bg-zinc-200" aria-hidden="true">
                                                <span
                                                    className={cn('block h-full rounded-full', groupSet === group.items.length ? 'bg-emerald-500' : 'bg-zinc-500')}
                                                    style={{ width: `${(groupSet / group.items.length) * 100}%` }}
                                                />
                                            </span>
                                            {groupSet}/{group.items.length}
                                        </span>
                                    )}
                                </button>
                            )}
                            {!collapsed && group.visible.length > 0 && (
                                <dl className={cn('divide-y divide-zinc-100', !group.title && 'border-t border-zinc-100')}>
                                    {group.visible.map(row)}
                                </dl>
                            )}
                        </div>
                    );
                })}
                {filtering && visibleGroups.length === 0 && (
                    <p className="border-t border-zinc-100 px-3 py-6 text-[13px] text-zinc-500">
                        {missingOnly && !propertyQuery
                            ? 'Every property has a value.'
                            : `No properties match "${propertySearch.trim()}".`}
                    </p>
                )}
            </div>
        </section>
    );
});

export default ValuesPanel;
